<template>
  <div class="editor-container">
    <div class="tiptap-editor-wrapper" :class="{ 'is-readonly': isReadOnly }">
      <div v-if="allowFormatting" class="tiptap-toolbar row items-center no-wrap">
        <div class="toolbar-group row items-center q-gutter-xs">
          <q-btn
            flat
            dense
            icon="format_bold"
            :color="editor && editor.isActive('bold') ? 'primary' : 'grey-7'"
            @click="runEditorCommand('toggleBold')"
          />
          <q-btn
            flat
            dense
            icon="format_italic"
            :color="editor && editor.isActive('italic') ? 'primary' : 'grey-7'"
            @click="runEditorCommand('toggleItalic')"
          />
          <q-btn
            flat
            dense
            icon="format_underlined"
            :color="editor && editor.isActive('underline') ? 'primary' : 'grey-7'"
            @click="runEditorCommand('toggleUnderline')"
          />
          <q-btn
            flat
            dense
            icon="format_clear"
            color="grey-7"
            @click="clearFormatting"
          >
            <q-tooltip>Remover formatação</q-tooltip>
          </q-btn>
        </div>

        <q-separator vertical class="toolbar-separator" />

        <div class="toolbar-group row items-center q-gutter-xs">
          <q-btn
            flat
            dense
            icon="format_align_left"
            :color="editor && editor.isActive({ textAlign: 'left' }) ? 'primary' : 'grey-7'"
            @click="setEditorAlign('left')"
          />
          <q-btn
            flat
            dense
            icon="format_align_center"
            :color="editor && editor.isActive({ textAlign: 'center' }) ? 'primary' : 'grey-7'"
            @click="setEditorAlign('center')"
          />
          <q-btn
            flat
            dense
            icon="format_align_right"
            :color="editor && editor.isActive({ textAlign: 'right' }) ? 'primary' : 'grey-7'"
            @click="setEditorAlign('right')"
          />
          <q-btn
            flat
            dense
            icon="format_align_justify"
            :color="editor && editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'grey-7'"
            @click="setEditorAlign('justify')"
          />
        </div>

        <q-separator vertical class="toolbar-separator" />

        <div class="toolbar-group row items-center q-gutter-xs">
          <q-btn
            flat
            dense
            icon="format_list_bulleted"
            :color="editor && editor.isActive('bulletList') ? 'primary' : 'grey-7'"
            @click="runEditorCommand('toggleBulletList')"
          />
          <q-btn
            flat
            dense
            icon="format_list_numbered"
            :color="editor && editor.isActive('orderedList') ? 'primary' : 'grey-7'"
            @click="runEditorCommand('toggleOrderedList')"
          />
          <q-btn
            flat
            dense
            icon="link"
            :color="editor && editor.isActive('link') ? 'primary' : 'grey-7'"
            @click="onLinkClick"
          />
        </div>

        <q-separator vertical class="toolbar-separator" />

        <div class="toolbar-group row items-center q-gutter-xs">
          <q-btn
            flat
            dense
            color="grey-7"
            @click="decreaseFontSize"
          >
            <svg class="font-size-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" aria-hidden="true">
              <path d="m40-200 210-560h100l210 560h-96l-51-143H187l-51 143H40Zm176-224h168l-82-232h-4l-82 232Zm384-16v-80h320v80H600Z"/>
            </svg>
            <q-tooltip>Diminuir tamanho da fonte</q-tooltip>
          </q-btn>
          <q-btn
            flat
            dense
            color="grey-7"
            @click="increaseFontSize"
          >
            <svg class="font-size-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" aria-hidden="true">
              <path d="m40-200 210-560h100l210 560h-96l-51-143H187l-51 143H40Zm176-224h168l-82-232h-4l-82 232Zm504 104v-120H600v-80h120v-120h80v120h120v80H800v120h-80Z"/>
            </svg>
            <q-tooltip>Aumentar tamanho da fonte</q-tooltip>
          </q-btn>
          <q-select
            v-model="selectedFontFamily"
            dense
            outlined
            emit-value
            map-options
            :options="fontFamilyOptions"
            @input="onFontFamilyChange"
            style="min-width: 150px; max-width: 180px"
          >
            <template v-slot:prepend>
              <q-icon name="text_fields" size="18px" />
            </template>
            <template v-slot:option="scope">
              <q-item
                v-bind="scope.itemProps"
                v-on="scope.itemEvents"
                :style="{ fontFamily: scope.opt.value }"
              >
                <q-item-section>
                  <q-item-label>{{ scope.opt.label }}</q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>
        </div>
      </div>
      <editor-content
        v-if="editor"
        class="tiptap-editor-content"
        :editor="editor"
      />
      <bubble-menu
        v-if="editor && allowFormatting"
        :editor="editor"
        :tippy-options="{ duration: 100, placement: 'top' }"
        :should-show="({ editor, view, state, oldState, from, to }) => {
          return from !== to && editor.isActive('link')
        }"
      >
        <div class="bubble-menu-link">
          <q-input
            v-model="linkUrl"
            dense
            outlined
            placeholder="Cole o link"
            bg-color="white"
            @keyup.enter="setLink"
            style="min-width: 300px"
          >
            <template v-slot:prepend>
              <q-icon name="link" />
            </template>
            <template v-slot:append>
              <q-btn
                flat
                dense
                round
                icon="check"
                size="sm"
                color="primary"
                @click="setLink"
              />
              <q-btn
                flat
                dense
                round
                icon="link_off"
                size="sm"
                color="negative"
                @click="removeLink"
              />
            </template>
          </q-input>
        </div>
      </bubble-menu>
      <div class="tiptap-footer text-caption text-grey-6 q-mt-xs">
        Digite @ para inserir variáveis | Use ↑↓ para navegar, Enter para selecionar
      </div>
    </div>
  </div>
</template>

<script>
import { Component, Vue, Watch, Prop } from 'vue-property-decorator'
import { getVariableColorScheme } from '../utils/variableColorScheme.js'
import '../utils/variableColorScheme.css'
import { Editor, EditorContent, BubbleMenu, VueRenderer } from '@tiptap/vue-2'
import { Extension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import TextStyle from '@tiptap/extension-text-style'
import FontFamily from '@tiptap/extension-font-family'
import Link from '@tiptap/extension-link'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import MentionList from './MentionList.vue'

const FontSize = Extension.create({
  name: 'fontSize',

  addOptions () {
    return {
      types: ['textStyle']
    }
  },

  addGlobalAttributes () {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize,
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {}
              }
              return {
                style: `font-size: ${attributes.fontSize}`
              }
            }
          }
        }
      }
    ]
  },

  addCommands () {
    return {
      setFontSize: fontSize => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize }).run()
      },
      unsetFontSize: () => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run()
      }
    }
  }
})

@Component({
  name: 'TemplateEditor',
  components: {
    EditorContent,
    BubbleMenu
  }
})
class TemplateEditor extends Vue {
  @Prop({ type: String, default: '' })
  content

  @Prop({ type: Array, default: () => [] })
  mentionItems

  @Prop({ type: Array, default: () => [] })
  messageVariables

  @Prop({ type: Boolean, default: false })
  isReadOnly

  @Prop({ type: Boolean, default: true })
  allowFormatting

  editor = null
  selectedFontSize = null
  selectedFontFamily = null
  showLinkInput = false
  linkUrl = ''

  get fontSizeOptions () {
    return [
      { label: this.$gettext('FONT_SIZE_DEFAULT'), value: null },
      { label: this.$gettext('FONT_SIZE_VERY_SMALL'), value: '10px' },
      { label: this.$gettext('FONT_SIZE_SMALL'), value: '13px' },
      { label: this.$gettext('FONT_SIZE_NORMAL'), value: '16px' },
      { label: this.$gettext('FONT_SIZE_MEDIUM'), value: '18px' },
      { label: this.$gettext('FONT_SIZE_LARGE'), value: '24px' },
      { label: this.$gettext('FONT_SIZE_VERY_LARGE'), value: '32px' },
      { label: this.$gettext('FONT_SIZE_HUGE'), value: '48px' }
    ]
  }

  fontFamilyOptions = [
    { label: 'Sans Serif', value: null },
    { label: 'Arial', value: 'Arial, sans-serif' },
    { label: 'Georgia', value: 'Georgia, serif' },
    { label: 'Courier New', value: '"Courier New", monospace' },
    { label: 'Comic Sans', value: '"Comic Sans MS", cursive' },
    { label: 'Impact', value: 'Impact, fantasy' },
    { label: 'Verdana', value: 'Verdana, sans-serif' }
  ]

  mounted () {
    this.createEditor()
  }

  beforeDestroy () {
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
  }

  escapeHtml (value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  getVariableByTechnicalKey (technicalKey) {
    if (!technicalKey || !this.messageVariables) {
      return null
    }
    // Match case-insensitive
    const keyLower = String(technicalKey).toLowerCase()
    for (const category of this.messageVariables) {
      if (!Array.isArray(category.variables)) {
        continue
      }
      const variable = category.variables.find(v => String(v.technicalKey).toLowerCase() === keyLower)
      if (variable) {
        return variable
      }
    }
    return null
  }

  buildVariableChipHtml (technicalKey) {
    const variable = this.getVariableByTechnicalKey(technicalKey)
    const variableType = variable?.variableType || null
    const colorClass = getVariableColorScheme(variableType).colorClass
    const label = variable?.displayLabel || variable?.displayTag || technicalKey
    const safeKey = this.escapeHtml(technicalKey)
    const safeLabel = this.escapeHtml(label)
    const safeType = variableType ? this.escapeHtml(variableType) : null

    return `<span class="editor-variable-chip ${colorClass}" data-chip="${safeKey}"${safeType ? ` data-variable-type="${safeType}"` : ''} contenteditable="false">${safeLabel}</span>`
  }

  /**
   * Converte {{$KEY}} para chips
   */
  convertTemplatePlaceholdersToEditorHtml (html) {
    const sourceHtml = String(html || '')
    const parser = new window.DOMParser()
    const doc = parser.parseFromString(`<div>${sourceHtml}</div>`, 'text/html')
    const root = doc.body.firstElementChild || doc.body

    const walker = doc.createTreeWalker(root, window.NodeFilter.SHOW_TEXT)
    const textNodes = []
    let node = walker.nextNode()
    while (node) {
      if (node.nodeValue && node.nodeValue.includes('{{$')) {
        textNodes.push(node)
      }
      node = walker.nextNode()
    }
    const placeholderRegex = /\{\{\$([A-Za-z0-9_]+)\}\}/g
    textNodes.forEach(textNode => {
      const rawText = textNode.nodeValue || ''
      let hasMatch = false
      let lastIndex = 0
      const fragment = doc.createDocumentFragment()
      let match = placeholderRegex.exec(rawText)
      while (match) {
        hasMatch = true
        const fullMatch = match[0]
        const technicalKey = match[1]
        if (match.index > lastIndex) {
          fragment.appendChild(doc.createTextNode(rawText.slice(lastIndex, match.index)))
        }
        const tempWrapper = doc.createElement('div')
        tempWrapper.innerHTML = this.buildVariableChipHtml(technicalKey)
        if (tempWrapper.firstChild) {
          fragment.appendChild(tempWrapper.firstChild)
        }
        lastIndex = match.index + fullMatch.length
        match = placeholderRegex.exec(rawText)
      }
      if (!hasMatch) {
        return
      }
      if (lastIndex < rawText.length) {
        fragment.appendChild(doc.createTextNode(rawText.slice(lastIndex)))
      }
      textNode.parentNode.replaceChild(fragment, textNode)
      placeholderRegex.lastIndex = 0
    })
    return root.innerHTML
  }

  convertEditorHtmlToTemplatePlaceholders (html) {
    const sourceHtml = String(html || '')
    if (!sourceHtml.includes('data-chip=')) {
      return this.preserveEmptyLinesForStorage(sourceHtml)
    }

    const parser = new window.DOMParser()
    const doc = parser.parseFromString(`<div>${sourceHtml}</div>`, 'text/html')
    const root = doc.body.firstElementChild || doc.body
    const chips = root.querySelectorAll('span[data-chip]')

    chips.forEach(chip => {
      const technicalKey = chip.getAttribute('data-chip')
      if (!technicalKey) {
        return
      }
      chip.replaceWith(doc.createTextNode(`{{$${technicalKey}}}`))
    })

    return this.preserveEmptyLinesForStorage(root.innerHTML)
  }

  preserveEmptyLinesForStorage (html) {
    const source = String(html || '')
    // Evita perda de quebras de linha ao persistir parágrafos vazios.
    return source
      .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '<p>&nbsp;</p>')
      .replace(/<p>\s*<\/p>/gi, '<p>&nbsp;</p>')
  }

  getSerializedContent () {
    if (!this.editor) {
      return this.convertEditorHtmlToTemplatePlaceholders(this.content || '')
    }
    return this.convertEditorHtmlToTemplatePlaceholders(this.editor.getHTML())
  }

  createEditor (initialContent = null) {
    const getVariableLabelById = this.getVariableLabelById.bind(this)
    const allowFormatting = this.allowFormatting
    const mentionExtension = Mention.extend({
      addAttributes () {
        return {
          id: {
            default: null,
            parseHTML: element => element.getAttribute('data-chip')
          },
          label: {
            default: null,
            parseHTML: element => {
              const textLabel = (element.textContent || '').trim()
              return textLabel || null
            }
          },
          variableType: {
            default: null,
            parseHTML: element => element.getAttribute('data-variable-type')
          },
          colorClass: {
            default: null,
            parseHTML: element => {
              const classAttr = element.getAttribute('class') || ''
              const classMatch = classAttr.match(/\bchip-[\w-]+\b/)
              return classMatch ? classMatch[0] : null
            }
          }
        }
      },
      parseHTML () {
        return [{ tag: 'span[data-chip]' }]
      },
      renderHTML ({ node }) {
        // Busca o label atual da variável para refletir mudanças futuras
        const variableInfo = this.options.editorInstance.getVariableByTechnicalKey(node.attrs.id)
        const variableType = node.attrs.variableType || variableInfo?.variableType || null
        const currentLabel = getVariableLabelById(node.attrs.id) || node.attrs.label || node.attrs.id || ''
        const typeColorClass = getVariableColorScheme(variableType).colorClass
        const colorClass = typeColorClass || node.attrs.colorClass || ''
        return [
          'span',
          {
            class: `editor-variable-chip ${colorClass}`.trim(),
            'data-chip': node.attrs.id,
            'data-variable-type': variableType || undefined,
            contenteditable: 'false'
          },
          currentLabel
        ]
      },
      renderText ({ node }) {
        return getVariableLabelById(node.attrs.id) || node.attrs.label || node.attrs.id || ''
      }
    }).configure({
      editorInstance: this,
      suggestion: this.getMentionSuggestion()
    })

    const starterKitOptions = {
      bold: allowFormatting,
      italic: allowFormatting,
      strike: allowFormatting,
      bulletList: allowFormatting,
      orderedList: allowFormatting,
      listItem: allowFormatting,
      heading: allowFormatting,
      blockquote: allowFormatting,
      code: allowFormatting,
      codeBlock: allowFormatting,
      horizontalRule: false
    }

    const extensions = [
      StarterKit.configure(starterKitOptions),
      Placeholder.configure({ placeholder: 'Digite a mensagem aqui...' }),
      mentionExtension
    ]

    if (allowFormatting) {
      extensions.push(
        Underline,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Link.configure({ openOnClick: false }),
        TextStyle,
        FontFamily,
        FontSize
      )
    }

    this.editor = new Editor({
      content: this.convertTemplatePlaceholdersToEditorHtml(initialContent !== null ? initialContent : (this.content || '')),
      editable: !this.isReadOnly,
      extensions,
      onUpdate: ({ editor }) => {
        this.$emit('update:content', this.convertEditorHtmlToTemplatePlaceholders(editor.getHTML()))
      }
    })
  }

  getMentionSuggestion () {
    const getMentionAttrs = this.getMentionAttrs.bind(this)

    return {
      char: '@',
      allowSpaces: false,

      items: ({ query }) => {
        const normalized = (query || '').toLowerCase()
        const items = this.mentionItems.map(item => ({
          ...item,
          colorClass: getVariableColorScheme(item).colorClass
        }))

        if (!normalized) {
          return items.slice(0, 50)
        }

        return items.filter(item => {
          const label = (item.label || '').toLowerCase()
          const key = (item.id || '').toLowerCase()
          return label.includes(normalized) || key.includes(normalized)
        }).slice(0, 50)
      },

      command: ({ editor, range, props }) => {
        const attrs = getMentionAttrs(props)
        editor.chain().focus().insertContentAt(range, [
          { type: 'mention', attrs }
        ]).run()
      },

      render: () => {
        let component

        return {
          onStart: props => {
            component = new VueRenderer(MentionList, {
              parent: this,
              propsData: props
            })

            if (!props.clientRect) {
              return
            }

            component.element.style.position = 'absolute'
            component.element.style.zIndex = '9999'
            document.body.appendChild(component.element)

            const rect = props.clientRect()
            if (rect) {
              component.element.style.top = `${rect.bottom + window.scrollY}px`
              component.element.style.left = `${rect.left + window.scrollX}px`
            }
          },

          onUpdate (props) {
            component.updateProps(props)

            if (!props.clientRect) {
              return
            }

            const rect = props.clientRect()
            if (rect) {
              component.element.style.top = `${rect.bottom + window.scrollY}px`
              component.element.style.left = `${rect.left + window.scrollX}px`
            }
          },

          onKeyDown (props) {
            if (props.event.key === 'Escape') {
              component.destroy()
              return true
            }

            return component.ref?.onKeyDown(props)
          },

          onExit () {
            if (component && component.element) {
              component.element.remove()
              component.destroy()
            }
          }
        }
      }
    }
  }

  getMentionAttrs (variable) {
    const colorScheme = getVariableColorScheme(variable)
    return {
      id: variable.technicalKey || variable.id,
      label: variable.displayLabel || variable.label || variable.id,
      variableType: variable.variableType,
      colorClass: colorScheme.colorClass
    }
  }

  getVariableLabelById (technicalKey) {
    // Busca o displayLabel atual da variável para refletir mudanças futuras
    if (!technicalKey || !this.messageVariables) {
      return null
    }

    for (const category of this.messageVariables) {
      if (!Array.isArray(category.variables)) {
        continue
      }
      const variable = category.variables.find(v => v.technicalKey === technicalKey)
      if (variable) {
        return variable.displayLabel || variable.displayTag || null
      }
    }

    return null
  }

  runEditorCommand (command) {
    if (!this.editor || this.isReadOnly) {
      return
    }

    const chain = this.editor.chain().focus()
    if (typeof chain[command] === 'function') {
      chain[command]().run()
    }
  }

  setEditorAlign (alignment) {
    if (!this.editor || this.isReadOnly) {
      return
    }
    this.editor.chain().focus().setTextAlign(alignment).run()
  }

  toggleHeading (level) {
    if (!this.editor || this.isReadOnly) {
      return
    }
    this.editor.chain().focus().toggleHeading({ level }).run()
  }

  onFontSizeChange (value) {
    if (!this.editor || this.isReadOnly || !this.allowFormatting) {
      return
    }
    if (!value) {
      this.editor.chain().focus().unsetFontSize().run()
    } else {
      this.editor.chain().focus().setFontSize(value).run()
    }
  }

  getFontSizeScale () {
    return ['10px', '13px', '16px', '18px', '24px', '32px', '48px']
  }

  getCurrentFontSizeOrDefault () {
    if (!this.editor) {
      return '16px'
    }

    const current = this.editor.getAttributes('textStyle')?.fontSize
    if (!current) {
      return '16px'
    }

    const normalized = String(current).trim().toLowerCase()
    return normalized
  }

  increaseFontSize () {
    if (!this.editor || this.isReadOnly || !this.allowFormatting) {
      return
    }

    const scale = this.getFontSizeScale()
    const current = this.getCurrentFontSizeOrDefault()
    const index = scale.indexOf(current)
    const safeIndex = index >= 0 ? index : 2
    const nextIndex = Math.min(safeIndex + 1, scale.length - 1)
    const nextSize = scale[nextIndex]

    this.selectedFontSize = nextSize
    this.onFontSizeChange(nextSize)
  }

  decreaseFontSize () {
    if (!this.editor || this.isReadOnly || !this.allowFormatting) {
      return
    }

    const scale = this.getFontSizeScale()
    const current = this.getCurrentFontSizeOrDefault()
    const index = scale.indexOf(current)
    const safeIndex = index >= 0 ? index : 2
    const prevIndex = Math.max(safeIndex - 1, 0)
    const prevSize = scale[prevIndex]

    this.selectedFontSize = prevSize
    this.onFontSizeChange(prevSize)
  }

  onFontFamilyChange (value) {
    if (!this.editor || this.isReadOnly || !this.allowFormatting) {
      return
    }
    if (!value) {
      this.editor.chain().focus().unsetFontFamily().run()
    } else {
      this.editor.chain().focus().setFontFamily(value).run()
    }
  }

  onLinkClick () {
    if (!this.editor || this.isReadOnly || !this.allowFormatting) {
      return
    }
    const previousUrl = this.editor.getAttributes('link').href || ''
    this.linkUrl = previousUrl
    if (previousUrl) {
      // Se já tem link, apenas garante que o bubble menu apareça
      return
    }
    // Se não tem link, define um link vazio para trigger o bubble menu
    this.editor.chain().focus().setLink({ href: '#' }).run()
  }

  setLink () {
    if (!this.editor || this.isReadOnly) {
      return
    }
    const url = this.linkUrl.trim()
    if (!url) {
      return
    }
    this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  removeLink () {
    if (!this.editor || this.isReadOnly) {
      return
    }
    this.editor.chain().focus().unsetLink().run()
    this.linkUrl = ''
  }

  openHtmlEditor () {
    if (!this.editor || this.isReadOnly || !this.allowFormatting) {
      return
    }
    const currentHtml = this.editor.getHTML()
    this.$q.dialog({
      title: 'Editar HTML',
      message: 'Codigo HTML',
      prompt: {
        model: currentHtml,
        type: 'textarea'
      },
      cancel: true,
      persistent: true
    }).onOk(html => {
      const nextHtml = String(html || '')
      this.editor.commands.setContent(nextHtml, false)
    })
  }

  clearFormatting () {
    if (!this.editor) {
      return
    }

    let chain = this.editor.chain().focus()
    if (typeof chain.clearNodes === 'function') {
      chain = chain.clearNodes()
    }
    if (typeof chain.unsetAllMarks === 'function') {
      chain = chain.unsetAllMarks()
    }
    if (typeof chain.setTextAlign === 'function') {
      chain = chain.setTextAlign('left')
    }
    chain.run()
  }

  getEditorHtml () {
    return this.getSerializedContent()
  }

  getEditorPlainText () {
    if (this.editor) {
      return this.editor.getText().trim()
    }
    return (this.content || '').replace(/<[^>]*>/g, '').trim()
  }

  insertVariablePlaceholder (variable) {
    if (!this.editor) {
      return
    }

    try {
      const mentionAttrs = this.getMentionAttrs(variable)
      this.editor.chain().focus().insertContent({
        type: 'mention',
        attrs: mentionAttrs
      }).run()
    } catch (error) {
      console.warn('Erro ao inserir variável no editor:', error)
    }
  }

  @Watch('content')
  onContentChange (newVal) {
    if (!this.editor) {
      return
    }
    const serializedCurrent = this.getSerializedContent()
    const nextValue = String(newVal || '')
    if (serializedCurrent !== nextValue) {
      this.editor.commands.setContent(this.convertTemplatePlaceholdersToEditorHtml(nextValue), false)
    }
  }

  @Watch('isReadOnly')
  onReadOnlyChange (newVal) {
    if (this.editor) {
      this.editor.setEditable(!newVal)
    }
  }

  @Watch('allowFormatting')
  onAllowFormattingChange () {
    const currentHtml = this.editor ? this.editor.getHTML() : (this.content || '')
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
    this.createEditor(currentHtml)
    if (!this.allowFormatting) {
      this.clearFormatting()
    }
  }
}

export default TemplateEditor
</script>

<style lang="stylus" scoped>
.editor-container
  display: flex
  flex-direction: column

.tiptap-editor-wrapper
  border: 1px solid #e0e0e0
  border-radius: 6px
  padding: 8px
  background-color: #ffffff
  height: 360px
  display: flex
  flex-direction: column

.tiptap-toolbar
  border-bottom: 1px solid #e0e0e0
  padding-bottom: 6px
  margin-bottom: 6px
  overflow-x: auto
  flex-shrink: 0

.toolbar-group
  flex-shrink: 0

.toolbar-separator
  margin: 0 8px
  flex-shrink: 0

.toolbar-row
  flex-wrap: wrap

.tiptap-editor-content
  flex: 1
  min-height: 0
  overflow-y: auto

.tiptap-footer
  flex-shrink: 0
  margin-top: 6px

.font-size-icon
  width: 20px
  height: 20px
  display: block

.font-size-icon path
  fill: currentColor

.bubble-menu-link {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
</style>

<style src="src/styles/tiptap-styles.css"></style>
